import { useEffect, useRef } from 'react'
import { saveColumnSettingsAPI, getColumnSettingsAPI } from '../lib/api/api'
import { useFilterStore } from '../services/filters'

let normalizeOrder = (order) => {
  if (!order) {
    return null
  }
  if (Array.isArray(order) && order.length === 0) {
    return null
  }
  return Array.isArray(order) ? order : null
}

let normalizeHidden = (hidden) => {
  if (!hidden) {
    return []
  }
  return Array.isArray(hidden) ? hidden : []
}

let normalizeSizing = (sizing) => {
  if (!sizing || typeof sizing !== 'object') {
    return {}
  }
  return sizing
}

let parseMaybeJson = (value) => {
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return value
    }
  }
  return value
}

export default function useColumnSettingsSync({
  module,
  columnOrder,
  hiddenColumnIds,
  columnSizing,
  debounceMs = 400,
}) {
  let lastPayloadRef = useRef(null)
  let timeoutRef = useRef(null)

  let setRowsEdited = useFilterStore(state => state.setRowsEdited)
  let selectedPresetId = useFilterStore(state => state.selectedPresetId)
  let rowsEditedBlockedUntil = useFilterStore(state => state.rowsEditedBlockedUntil)

  // Keep volatile values in refs so they're always current inside the timeout
  // without adding them to the effect's dependency array
  let selectedPresetIdRef = useRef(selectedPresetId)
  let rowsEditedBlockedUntilRef = useRef(rowsEditedBlockedUntil)

  useEffect(() => { selectedPresetIdRef.current = selectedPresetId })
  useEffect(() => { rowsEditedBlockedUntilRef.current = rowsEditedBlockedUntil })

  useEffect(() => {
    if (!module) {
      return
    }

    let payload = {
      'col-order': normalizeOrder(columnOrder),
      'col-hidden': normalizeHidden(hiddenColumnIds),
      'col-sizing': normalizeSizing(columnSizing),
    }

    let serialized = JSON.stringify(payload)
    if (serialized === lastPayloadRef.current) {
      return
    }

    lastPayloadRef.current = serialized

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(async () => {
      try {
        await saveColumnSettingsAPI(module, payload)
        // Mark rows as edited vs. the active preset — unless we're still
        // inside the suppress window that was set when a preset was applied
        if (selectedPresetIdRef.current && Date.now() > rowsEditedBlockedUntilRef.current) {
          setRowsEdited(true)
        }
      } catch (error) {
        console.error('Failed to persist column settings', error)
      }
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [module, columnOrder, hiddenColumnIds, columnSizing, debounceMs, setRowsEdited])
}

export function useColumnSettingsLoader({
  module,
  setColumnOrder,
  setHiddenColumnIds,
  setColumnSizing,
  onLoaded,
}) {
  let columnSettingsVersion = useFilterStore(state => state.columnSettingsVersion)
  // onLoaded should only fire once per module, not on every version bump
  let hasCalledOnLoaded = useRef(false)

  useEffect(() => {
    hasCalledOnLoaded.current = false
  }, [module])

  useEffect(() => {
    if (!module) {
      return
    }

    let cancelled = false
    ;(async () => {
      try {
        let response = await getColumnSettingsAPI(module)
        if (cancelled) {
          return
        }
        if (!response) {
          return
        }

        let remoteOrder = parseMaybeJson(response['col-order'])
        let remoteHidden = parseMaybeJson(response['col-hidden'])
        let remoteSizing = parseMaybeJson(response['col-sizing'])

        if (setColumnOrder) {
          if (remoteOrder == null) {
            setColumnOrder(remoteOrder)
          } else {
            let normalizedOrder = Array.isArray(remoteOrder) ? remoteOrder : []
            setColumnOrder(normalizedOrder)
          }
        }

        if (setHiddenColumnIds) {
          let normalizedHidden = normalizeHidden(remoteHidden)
          setHiddenColumnIds(normalizedHidden)
        }

        if (setColumnSizing) {
          let normalizedSizing = normalizeSizing(remoteSizing)
          setColumnSizing(normalizedSizing)
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Failed to load column settings', error)
        }
      } finally {
        if (!cancelled && !hasCalledOnLoaded.current) {
          hasCalledOnLoaded.current = true
          onLoaded?.()
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [module, setColumnOrder, setHiddenColumnIds, setColumnSizing, columnSettingsVersion])
}
